<script lang="ts">
  import { m } from "#messages";
  import { getLocale } from "#lib/locale.ts";
  import { FITTED } from "#lib/shots/bands.ts";
  import type { Hero } from "#lib/shots/bands.ts";
  import Shot from "#lib/shots/Shot.svelte";
  import { heroSources } from "#lib/shots/sources.ts";
  import Button from "#lib/ui/Button.svelte";
  import Icon from "#lib/ui/Icon.svelte";

  let {
    href,
    testId,
    hero,
  }: {
    href: string;
    testId: string;
    hero: Hero;
  } = $props();

  const fit = $derived(FITTED.includes(hero));
  const sources = $derived(heroSources(getLocale(), hero));
</script>

<div class="stage relative" data-testid={testId}>
  <div class="demo relative">
    <div class="well relative h-full overflow-hidden rounded-3xl p-4">
      <a
        {href}
        data-sveltekit-reload
        tabindex="-1"
        aria-hidden="true"
        class={["board block", fit && "fit"]}
        data-testid="{testId}-frame"
      >
        <Shot {sources} alt="" loading="eager" priority="high" class="block h-auto w-full" />
      </a>
      <div class="board-fade" aria-hidden="true"></div>
      <div class="tint" aria-hidden="true"></div>
      <div class="board-open">
        <Button variant="primary" size="lg" shape="round" {href} data-sveltekit-reload>
          {m.landing_live_open()}<Icon name="next" size={16} />
        </Button>
      </div>
    </div>
  </div>
</div>

<style>
  .stage {
    perspective: 1800px;
  }

  .demo {
    transform-origin: 50% 0%;
  }

  .well {
    background: color-mix(in srgb, var(--surface-2) 55%, transparent);
    box-shadow:
      0 0 0 1px var(--edge),
      0 40px 120px -40px rgb(0 0 0 / 0.8);
  }

  .board {
    height: 34rem;
    overflow: hidden;
  }

  @media (width < 48rem) {
    .fit {
      height: auto;
      max-height: 34rem;
    }
  }

  @media (width < 40rem) {
    .fit + .board-fade {
      display: none;
    }
  }

  .board-fade {
    position: absolute;
    inset: auto 0 0;
    height: 12rem;
    background: linear-gradient(
      to bottom,
      transparent,
      color-mix(in srgb, var(--surface-2) 55%, var(--surface-0)) 85%
    );
    pointer-events: none;
  }

  .tint {
    position: absolute;
    inset: 0;
    border-radius: 1.5rem;
    background: color-mix(in srgb, var(--surface-0) 40%, transparent);
    opacity: 0;
    transition: opacity 220ms ease-out;
    pointer-events: none;
  }

  .board-open {
    position: absolute;
    bottom: 2.5rem;
    left: 50%;
    translate: -50% 0;
    transition: scale 260ms cubic-bezier(0.2, 0.7, 0.2, 1);
  }

  .demo:hover .tint,
  .demo:focus-within .tint {
    opacity: 1;
  }

  .demo:hover .board-open,
  .demo:focus-within .board-open {
    scale: 1.06;
  }

  @supports (animation-timeline: view()) {
    @media (prefers-reduced-motion: no-preference) and (width >= 48rem) {
      .demo {
        animation: unfold linear both;
        animation-timeline: view();
        animation-range: cover 0% cover 70%;
      }
    }
  }

  @keyframes unfold {
    from {
      transform: rotateX(32deg) scale(0.86) translateY(-1rem);
    }
    55% {
      transform: none;
    }
    to {
      transform: scale(1.07);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .tint,
    .board-open {
      transition: none;
    }
  }
</style>
